import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { getAllHealthHacks, getHealthHacksByCategory, HealthHack } from '../services/api';
import NewsletterSignup from '../components/NewsletterSignup';
import { useScrollToTop } from '../hooks/useScrollToTop';

const AllHealthHacksPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hacks, setHacks] = useState<HealthHack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hacksPerPage = 9;

  // Scroll to top when category changes
  useScrollToTop();

  // Fetch daily tips
  useEffect(() => {
    const fetchHacks = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let fetchedHacks: HealthHack[];
        
        if (selectedCategory === 'all') {
          fetchedHacks = await getAllHealthHacks();
        } else {
          fetchedHacks = await getHealthHacksByCategory(selectedCategory);
        }
        
        setHacks(fetchedHacks);
      } catch (err) {
        setError('Failed to load daily tips');
        console.error('Error fetching daily tips:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHacks();
  }, [selectedCategory]);

  // Pagination
  const totalPages = Math.ceil(hacks.length / hacksPerPage);
  const startIndex = (currentPage - 1) * hacksPerPage;
  const endIndex = startIndex + hacksPerPage;
  const currentHacks = hacks.slice(startIndex, endIndex);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when changing category
  };

  const categories = [
    { id: 'all', name: 'All Tips' },
    { id: 'stress-relief', name: 'Stress Relief' },
    { id: 'mindfulness', name: 'Mindfulness' },
    { id: 'sleep', name: 'Sleep Better' },
    { id: 'self-care', name: 'Self Care' },
    { id: 'wellness', name: 'Wellness' }
  ];

  const getCategoryName = (categoryId: string) => {
    const categories = {
      'stress-relief': 'Stress Relief',
      mindfulness: 'Mindfulness', 
      sleep: 'Sleep Better',
      'self-care': 'Self Care',
      wellness: 'Wellness'
    };
    return categories[categoryId as keyof typeof categories] || categoryId;
  };

  const getCategoryColor = (category: string) => {
    // Use consistent blue for all categories
    return 'category-badge';
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>All Daily Tips - Destress Daily</title>
          <meta name="description" content="Browse all daily tips, wellness advice, and stress relief strategies. Find practical wellness tips for every aspect of your life." />
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading daily tips...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Destress Tips - Daily Stress Relief Strategies | Destress Daily</title>
        <meta name="description" content="Browse all destress tips, stress relief strategies, and wellness advice. Find practical destress techniques for every aspect of your life." />
        <meta name="keywords" content="destress tips, all destress tips, stress relief strategies, destress techniques, wellness advice, stress management, mindfulness, self care, sleep optimization, how to destress" />
        <meta property="og:title" content="All Destress Tips - Daily Stress Relief Strategies | Destress Daily" />
        <meta property="og:description" content="Browse all destress tips, stress relief strategies, and wellness advice." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://destress-daily.com/tips" />
        <link rel="canonical" href="https://destress-daily.com/tips" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                All Daily Tips
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Discover our complete collection of practical health tips, wellness hacks, and evidence-based advice to improve every aspect of your life.
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Daily Tips Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Results Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {selectedCategory === 'all' ? 'All Daily Tips' : getCategoryName(selectedCategory) + ' Daily Tips'}
              </h2>
              <p className="text-lg text-gray-600">
                {hacks.length} daily tip{hacks.length !== 1 ? 's' : ''} found
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-center py-12">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Error Loading Daily Tips</h3>
                <p className="text-gray-600 mb-8">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Daily Tips Grid */}
            {!error && currentHacks.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-gray-900 mb-4">No Daily Tips Found</h3>
                <p className="text-gray-600 mb-8">
                  {selectedCategory === 'all' 
                    ? 'No daily tips have been posted yet. Check back soon!' 
                    : `No daily tips found for ${getCategoryName(selectedCategory)}.`
                  }
                </p>
                {selectedCategory !== 'all' && (
                  <Link to="/tips" className="btn-primary">
                    View All Daily Tips
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentHacks.map((hack) => (
                    <article key={hack.id} className="health-hack-card group">
                      <div className="mb-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(hack.category)}`}>
                          {getCategoryName(hack.category)}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                        <Link to={`/tip/${hack.slug}`}>
                          {hack.title}
                        </Link>
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {hack.excerpt}
                      </p>
                      
                      {/* Tags */}
                      {hack.tags && hack.tags.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {hack.tags.slice(0, 3).map((tag, index) => (
                              <Link
                                key={index}
                                to={`/tag/${encodeURIComponent(tag)}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200"
                              >
                                #{tag}
                              </Link>
                            ))}
                            {hack.tags.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
                                +{hack.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {hack.read_time} min read
                        </div>
                        <Link 
                          to={`/tip/${hack.slug}`}
                          className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200"
                        >
                          Read More →
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg ${
                            currentPage === page
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Newsletter Signup */}
      <NewsletterSignup variant="hero" />
    </>
  );
};

export default AllHealthHacksPage; 